import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { alpha, Box, ButtonBase, Stack, Typography, type Theme } from '@mui/material';
import { defaultCryptoPaymentProvider } from '../../../../config/payment/crypto';
import { useInstallPrompt } from '../../../../contexts/InstallPromptContext';
import useLocalization from '../../../../hooks/useLocalization';
import NeobetFooterHeading from './NeobetFooterHeading';
import NeobetFooterLanguageSelect from './NeobetFooterLanguageSelect';

const AcceptedCurrencyIcon = defaultCryptoPaymentProvider.icon;

const styles = {
  utilitiesSection: (theme: Theme) => ({
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'stretch', md: 'flex-start' },
    gap: { xs: 3, md: 4 },
    py: { xs: 1.5, md: 2 },
    borderTop: '1px solid',
    borderColor: alpha(theme.palette.common.white, 0.08),
  }),
  currencyBlock: {
    minWidth: 0,
  },
  currencyList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.75,
  },
  currencyPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    minHeight: 34,
    px: 1.75,
    py: 1.5,
    borderRadius: 2,
    backgroundColor: 'neutral.700',
    color: 'neutral.100',
  },
  currencyIcon: {
    width: 18,
    height: 18,
    flexShrink: 0,
  },
  currencyLabel: {
    typography: 'body3',
    fontWeight: 800,
    lineHeight: 1,
  },
  languageBlock: {
    minWidth: { xs: 0, md: 190 },
    alignItems: { xs: 'flex-start', md: 'flex-end' },
  },
  languageInner: {
    width: '100%',
    maxWidth: { xs: '100%', md: 190 },
  },
  languageHeading: {
    textAlign: 'left',
  },
  installBlock: {
    minWidth: { xs: 0, md: 190 },
    alignItems: { xs: 'flex-start', md: 'flex-end' },
  },
  installIconButton: {
    width: 40,
    height: 40,
    borderRadius: 2,
    overflow: 'hidden',
    backgroundColor: 'neutral.600',
    color: 'text.primary',
    '&:hover': {
      backgroundColor: 'neutral.500',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
} as const;

const NeobetFooterUtilities = () => {
  const { t } = useLocalization();
  const { canShowInstallPrompt, showInstallPrompt } = useInstallPrompt();

  return (
    <Box sx={styles.utilitiesSection}>
      <Box sx={styles.currencyBlock}>
        <NeobetFooterHeading>{t('footer.acceptedCurrencies', 'Accepted Currencies')}</NeobetFooterHeading>
        <Box sx={styles.currencyList}>
          <Box sx={styles.currencyPill}>
            <AcceptedCurrencyIcon sx={styles.currencyIcon} />
            <Typography component='span' sx={styles.currencyLabel}>
              {defaultCryptoPaymentProvider.tokenLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Stack sx={styles.languageBlock}>
        <Box sx={styles.languageInner}>
          <NeobetFooterHeading sx={styles.languageHeading}>{t('language', 'Language')}</NeobetFooterHeading>
          <NeobetFooterLanguageSelect />
        </Box>
      </Stack>

      {canShowInstallPrompt && (
        <Stack sx={styles.installBlock}>
          <NeobetFooterHeading>{t('installAsApp', 'Install as App')}</NeobetFooterHeading>
          <ButtonBase
            sx={styles.installIconButton}
            onClick={showInstallPrompt}
            aria-label={t('installAsApp', 'Install as App')}
          >
            <DownloadRoundedIcon />
          </ButtonBase>
        </Stack>
      )}
    </Box>
  );
};

export default NeobetFooterUtilities;
